import {
  format,
  parseISO,
  isToday,
  differenceInDays,
  isWithinInterval,
} from "date-fns";
import {
  PaymentCalendarRed,
  PaymentCalendarBlue,
  PaymentCalendarBlack,
} from "../assets/svgs";
import { PAYMENT_CARD_STATUS } from "../constants";
import { getPngImages } from "./svgUtils";

export const getInitials = (name) => {
  if (name) {
    const nameParts = name.trim().split(" ");
    const firstInitial = nameParts[0][0].toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1][0].toUpperCase();
    return firstInitial + lastInitial;
  } else {
    return "";
  }
};

export const getDisplayName = (user) => {
  const firstName = user?.first_name ?? "_";
  const middleName = user?.middle_name ?? "_";
  const lastName = user?.last_name ?? "_";

  const displayName = `${firstName} ${middleName} ${lastName}`;

  return displayName;
};

export const stringToBoolean = (value) => {
  if (typeof value === "string") {
    const lowerValue = value.trim().toLowerCase();
    if (lowerValue === "true") {
      return true;
    } else if (lowerValue === "false") {
      return false;
    }
  }
  return false;
};

export const getPaymentCardDate = (dateString) => {
  const dateObject = parseISO(dateString);
  const day = format(dateObject, "dd");
  const month = format(dateObject, "MMM");
  return { day, month };
};

export const getPaymentCardStatus = ({ dueDateString, translate }) => {
  const dueDate = parseISO(dueDateString);
  const today = new Date();

  if (isToday(dueDate)) {
    return {
      label: `${translate("global.due_today")}`,
      key: PAYMENT_CARD_STATUS.DUE_TODAY,
    };
  }

  const difference = differenceInDays(dueDate, today);

  if (difference < 0) {
    return {
      label: `${translate("global.overdue")}`,
      key: PAYMENT_CARD_STATUS.OVERDUE,
    };
  } else if (difference > 0) {
    return {
      label: `${translate("global.due_in")} ${difference} ${translate(
        "global.days"
      )}`,
      key: PAYMENT_CARD_STATUS.DUE_IN_NTH_DAYS,
    };
  }
};

export const getPaymentCardStyles = (status) => {
  switch (status.key) {
    case PAYMENT_CARD_STATUS.OVERDUE:
      return {
        dataContainerBgColor: "bg-danger-100",
        statusContainerBgColor: "bg-danger-100",
        dayTextColor: "text-danger-700",
        monthTextColor: "text-danger-700",
        paymentStatusTextColor: "text-danger-700",
      };
    case PAYMENT_CARD_STATUS.DUE_TODAY:
      return {
        dataContainerBgColor: "bg-neutral-100",
        statusContainerBgColor: "bg-neutral-100",
        dayTextColor: "text-neutral-900",
        monthTextColor: "text-neutral-600",
        paymentStatusTextColor: "text-neutral-900",
      };
    case PAYMENT_CARD_STATUS.DUE_IN_NTH_DAYS:
      return {
        dataContainerBgColor: "bg-neutral-100",
        statusContainerBgColor: "bg-neutral-100",
        dayTextColor: "text-neutral-900",
        monthTextColor: "text-neutral-600",
        paymentStatusTextColor: "text-black",
      };
    default:
      return {
        dataContainerBgColor: "bg-neutral-100",
        statusContainerBgColor: "bg-neutral-100",
        dayTextColor: "text-neutral-900",
        monthTextColor: "text-neutral-600",
        paymentStatusTextColor: "text-black",
      };
  }
};

export const getPaymentCardIcon = (status) => {
  switch (status.key) {
    case PAYMENT_CARD_STATUS.OVERDUE:
      return PaymentCalendarRed;
    case PAYMENT_CARD_STATUS.DUE_TODAY:
      return PaymentCalendarBlue;
    case PAYMENT_CARD_STATUS.DUE_IN_NTH_DAYS:
      return PaymentCalendarBlack;
    default:
      return PaymentCalendarBlack;
  }
};

export const getOrdinal = (n) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = n % 100;

  if (value >= 11 && value <= 13) {
    return `${n}th`;
  }

  const suffix = suffixes[n % 10] || "th";
  return `${n}${suffix}`;
};

export const filterPaymentsByDateRange = (payments, dateRange) => {
  const { fromDate, toDate } = dateRange;

  if (!fromDate || !toDate) {
    return payments;
  }

  const parsedFromDate = parseISO(fromDate);
  const parsedToDate = parseISO(toDate);

  return payments.filter((payment) => {
    const dueDate = parseISO(payment.due_date);
    return isWithinInterval(dueDate, {
      start: parsedFromDate,
      end: parsedToDate,
    });
  });
};

export const parseDynamicObject = (data) => {
  return JSON.parse(
    data
      .replace(/(\w+):/g, '"$1":')
      .replace(/:\s*([^,}\s]+)\s+SAR/g, ': "$1 SAR"')
      .replace(/:\s*([^,}\s]+)%/g, ': "$1%"')
      .replace(/:\s*([^,}\s]+)\s+Months/g, ': "$1 Months"')
      .replace(/:\s*}/g, ": null}")
  );
};

export const parseDynamicArray = (data) => {
  return JSON.parse(
    data
      .replace(/(\b[a-zA-Z0-9_]+)(?=:)/g, '"$1"')
      .replace(/:\s*([0-9,]+\s+SAR)/g, ': "$1"')
      .replace(/:\s*(\d+)%/g, ': "$1%"')
      .replace(/:\s*(\d+)\s+Months/g, ': "$1 Months"')
      .replace(/(\b[a-zA-Z0-9_]+)(?=\s*:)/g, '"$1"')
      .replace(/:\s*([^",{}\[\]]+)(?=\s*[,}\]])/g, ': "$1"')
      .replace(/:\s*(?=[,}\]])/g, ": null")
  );
};

export const convertTimestamp = (timestamp) => {
  const seconds = Math.floor(timestamp / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  const displayHours = hours.toString().padStart(2, "0");
  const displayMinutes = (minutes % 60).toString().padStart(2, "0");
  const displaySeconds = (seconds % 60).toString().padStart(2, "0");

  return `${displayHours}:${displayMinutes}:${displaySeconds}`;
};

export const getParamsFromUrl = (url) => {
  url = decodeURI(url);
  if (typeof url === "string") {
    const [base, query] = url.split("?");
    if (!query) return {}; // No params present

    const eachParamsArr = query.split("&");
    const obj = {};

    eachParamsArr.forEach((param) => {
      const [key, value] = param.split("=");
      if (key) {
        obj[key] = value ?? "";
      }
    });

    return obj;
  }
  return {};
};

export const getColor = (color) => {
  switch (color) {
    case "green-50":
      return "#f0fdf4";
    case "blue-50":
      return "#eff6ff";
    case "gray-50":
      return "#f9fafb";
    default:
      return "text-danger-700";
  }
};

export const findUrlAttribute = (templateSchema) => {
  // Use iterative BFS (breadth-first search) instead of recursion to avoid stack limits
  // and to find the shallowest match faster
  const queue = [...templateSchema];

  while (queue.length) {
    const component = queue.shift();

    // Check for url attribute first (fast path)
    if (component?.attributes?.url) {
      return JSON.parse(component.attributes.url);
    }

    // Add child components to queue if they exist
    if (Array.isArray(component?.components)) {
      queue.push(...component.components);
    }
  }

  return null;
};

export function getPaymentLabel(no) {
  const num = parseInt(no, 10);
  const suffix =
    num % 10 === 1 && num !== 11
      ? "st"
      : num % 10 === 2 && num !== 12
      ? "nd"
      : num % 10 === 3 && num !== 13
      ? "rd"
      : "th";
  return `${num}${suffix}`;
}

export function getPaymentInfo(payment) {
  if (!payment || !payment) {
    throw new Error("payment.paymentDate is required");
  }

  const raw = String(payment).trim();

  const parts = raw.includes("/") ? raw.split("/") : raw.split("-");
  if (parts.length !== 3) {
    return { error: "Invalid date format. Use DD/MM/YYYY" };
  }

  const [dStr, mStr, yStr] = parts;
  const day = parseInt(dStr, 10);
  const month = parseInt(mStr, 10);
  const year = parseInt(yStr, 10);

  if (
    Number.isNaN(day) ||
    Number.isNaN(month) ||
    Number.isNaN(year) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return { error: "Invalid date parts" };
  }

  const paymentDate = new Date(year, month - 1, day);

  if (
    paymentDate.getFullYear() !== year ||
    paymentDate.getMonth() !== month - 1 ||
    paymentDate.getDate() !== day
  ) {
    return { error: "Invalid date (day/month/year mismatch)" };
  }

  const today = new Date();
  const todayMid = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );

  const diffMs = paymentDate.getTime() - todayMid.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const dueDays = Math.abs(diffDays);
  const isDue = diffDays > 0;
  const isOverdue = diffDays < 0;
  const isToday = diffDays === 0;

  const monthName = paymentDate.toLocaleString("default", { month: "short" });

  let statusText;
  if (isToday) statusText = "Due today";
  else if (isDue)
    statusText = `Due in ${dueDays} day${dueDays === 1 ? "" : "s"}`;
  else statusText = `Overdue by ${dueDays} day${dueDays === 1 ? "" : "s"}`;

  return {
    day: String(day).padStart(2, "0"),
    month: monthName,
    dueDays,
    isDue,
    isOverdue,
    isToday,
    statusText,
    paymentDate,
  };
}

export const safeJsonParse = (value, fallback = []) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  try {
    const sanitized = value
      .replace(/\r\n/g, "\\n")
      .replace(/\n/g, "\\n")
      .replace(/\r/g, "\\n");

    return JSON.parse(sanitized);
  } catch (error) {
    console.log("Error: ", error);
    return fallback;
  }
};

export const getApiErrorMessage = (error) => {
  if (!error) return "Something went wrong";

  // 1. message from API: error.response.data.errors[0].message
  const nestedMessage =
    error?.response?.data?.errors?.[0]?.message ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.message;

  if (nestedMessage) return nestedMessage;

  // 2. Axios default error message
  if (error?.message) return error.message;

  // 3. Fallback
  return "Something went wrong";
};

export const mapAssetsGrouped = (data) => {
  const groups = {};
  data.forEach((item) => {
    const key = `${item.vendor}|${item.assetBrand}|${item.assetType}`;
    if (!groups[key]) {
      groups[key] = {
        count: 0,
        totalLimit: 0,
        assetBrand: item.assetBrand,
        assetType: item.assetType,
        vendor: item.vendor,
      };
    }
    groups[key].count += 1;
    groups[key].totalLimit += Number(item.limitAmount);
  });
  return Object.values(groups).map((group) => ({
    label: `${group.count}x ${group.assetBrand} ${group.assetType}`,
    value: `SAR ${group.totalLimit}`,
  }));
};

export const filterRemainingAssets = (assets, selectedAssets) => {
  const flatAssets = assets.flat();
  return flatAssets.filter((asset) => {
    return !selectedAssets.some(
      (sel) =>
        sel.vendor === asset.vendor &&
        sel.assetBrand === asset.assetBrand &&
        sel.assetType === asset.assetType &&
        sel.limitAmount === asset.limitAmount &&
        sel.rate === asset.rate &&
        sel.tenor === asset.tenor
    );
  });
};
